import { Suspense } from 'react';
import { Icon } from '@chakra-ui/react';
import { Route, RouteProps, Switch, withRouter, Redirect } from 'react-router';
import { BrowserRouter as Router } from 'react-router-dom';
import {
  FaEnvelope,
  FaHome,
  FaRegBuilding,
  FaUserFriends,
} from 'react-icons/fa';
import { Loader, SideBar } from 'components';
import {
  PRIVATE_ROUTES,
  AUTH_ROUTES,
  ADMIN_ROUTES,
  PUBLIC_ROUTES,
  PDF_ROUTES,
} from './routes';
import { useAuthContext } from 'context';
import { isAdmin } from 'utils';

interface RouteType extends RouteProps {
  component: any;
}

export type NavItem = {
  to: string;
  title: string;
  icon: React.ReactNode;
  subMenu?: Array<{ to: string; title: string }>;
};

const NAV_ITEMS: NavItem[] = [
  {
    to: `/home`,
    title: 'Home',
    icon: <Icon as={FaHome} w={6} h={6} color="custom.levoBlue" />,
  },
  {
    to: `/admin/portal`,
    title: 'Users',
    icon: <Icon as={FaUserFriends} w={6} h={6} color="custom.levoBlue" />,
  },
  {
    to: `/admin/mail`,
    title: 'Mail',
    icon: <Icon as={FaEnvelope} w={6} h={6} color="custom.levoBlue" />,
  },
  {
    to: `/admin/agencies`,
    title: 'Agencies',
    icon: <Icon as={FaRegBuilding} w={6} h={6} color="custom.levoBlue" />,
  },
];

const PrivateRoute = ({ component: Component, ...rest }: RouteType) => {
  const { user, userSignedOut } = useAuthContext();
  return (
    <Route
      {...rest}
      render={(props) =>
        user ? (
          <Suspense fallback={<Loader />}>
            <Component {...rest} />
          </Suspense>
        ) : (
          <Redirect
            to={{
              pathname: '/',
              search: userSignedOut
                ? ''
                : `?redirectTo=${props.location.pathname}`,
              state: { from: userSignedOut ? undefined : props.location },
            }}
          />
        )
      }
    />
  );
};

const AdminRoute = ({ component: Component, ...rest }: RouteType) => {
  const { user, userSignedOut } = useAuthContext();
  return (
    <Route
      {...rest}
      render={(props) =>
        user ? (
          isAdmin(user) ? (
            <Suspense fallback={<Loader />}>
              <Component {...rest} />
            </Suspense>
          ) : (
            <Redirect
              to={{ pathname: '/home', state: { from: props.location } }}
            />
          )
        ) : (
          <Redirect
            to={{
              pathname: '/',
              search: userSignedOut
                ? ''
                : `?redirectTo=${props.location.pathname}`,
              state: { from: userSignedOut ? undefined : props.location },
            }}
          />
        )
      }
    />
  );
};

const AuthRoute = ({ component: Component, ...rest }: RouteType) => {
  const { user, signingUp } = useAuthContext();

  return (
    <Route
      {...rest}
      render={() =>
        user && !signingUp ? (
          <Redirect to={{ pathname: '/home' }} />
        ) : (
          <Suspense fallback={<Loader />}>
            <Component />
          </Suspense>
        )
      }
    />
  );
};

const PublicRoute = ({ component: Component, ...rest }: RouteType) => {
  return (
    <Route
      {...rest}
      render={() => (
        <Suspense fallback={<Loader />}>
          <Component />
        </Suspense>
      )}
    />
  );
};

const RoutesContainer = withRouter(() => {
  return (
    <Suspense fallback={<Loader />}>
      <Switch>
        {PUBLIC_ROUTES.map((route) => {
          return <PublicRoute key={route.path} {...route} />;
        })}
        {AUTH_ROUTES.map((route) => {
          return <AuthRoute key={route.path} {...route} />;
        })}
        {PRIVATE_ROUTES.map((route) => {
          return <PrivateRoute key={route.path} {...route} />;
        })}
        <Route
          path="/admin"
          render={() => (
            <SideBar
              bg="gray.900"
              color="white"
              navItems={NAV_ITEMS}
              hoverColor="gray.700"
              accentColor="cyan.500"
            >
              {ADMIN_ROUTES.map((route) => {
                return <AdminRoute key={route.path} {...route} />;
              })}
            </SideBar>
          )}
        />
        {PDF_ROUTES.map((route) => {
          return <AdminRoute key={route.path} {...route} />;
        })}
      </Switch>
    </Suspense>
  );
});

const Navigation = (): any => (
  <Router>
    <RoutesContainer />
  </Router>
);

export default Navigation;
